/**
 * Markdown renderer implementation using markdown-it and DOMPurify
 * Implements IMarkdownRenderer interface
 */

import MarkdownIt from 'markdown-it';
import markdownItAnchor from 'markdown-it-anchor';
import DOMPurify from 'isomorphic-dompurify';
import type {
  IMarkdownRenderer,
  RenderOptions,
  TOCEntry,
  TOCOptions,
} from '../../../contracts/markdown.ts';

const DEFAULT_WPM = 200; // Average reading speed: 200 words per minute

export class MarkdownRenderer implements IMarkdownRenderer {
  private md: MarkdownIt;

  constructor() {
    this.md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
      breaks: false,
    });

    // Add anchor plugin to automatically add IDs to headings
    this.md.use(markdownItAnchor, {
      permalink: false,
      slugify: (s: string) => this.slugify(s)
    });
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  render(markdown: string, options?: RenderOptions): string {
    // Update markdown-it options if provided
    if (options) {
      this.md.set({
        typographer: options.typographer ?? true,
        breaks: options.breaks ?? false,
      });
    }

    // Render markdown to HTML
    let html = this.md.render(markdown);

    // Apply base URL for relative links
    if (options?.baseUrl) {
      html = this.applyBaseUrl(html, options.baseUrl);
    }

    // Apply custom CSS classes
    if (options?.cssClasses) {
      html = this.applyCssClasses(html, options.cssClasses);
    }

    // Sanitize HTML
    html = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'em', 'u', 's', 'del', 'ins',
        'a', 'img',
        'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'blockquote', 'pre', 'code',
        'div', 'span',
        'sub', 'sup', // Subscript and superscript for FB2
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'id',
        'width', 'height', 'align',
      ],
    });

    return html;
  }

  extractTOC(markdown: string, options?: TOCOptions): TOCEntry[] {
    const maxLevel = options?.maxLevel ?? 3;
    const minLevel = options?.minLevel ?? 1;
    const generateIds = options?.generateIds ?? true;

    const toc: TOCEntry[] = [];
    const lines = markdown.split('\n');
    let position = 0;

    for (const line of lines) {
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const level = match[1]!.length;
        const text = match[2]!.trim();

        if (level >= minLevel && level <= maxLevel) {
          const id = generateIds ? this.slugify(text) : '';

          toc.push({
            id,
            text,
            level,
            children: [],
            position,
          });
        }
      }
      position++;
    }

    // Build hierarchical structure
    return this.buildHierarchy(toc);
  }

  getWordCount(markdown: string): number {
    // Remove markdown syntax - order matters!
    let text = markdown;

    // Remove code blocks first (multiline)
    text = text.replace(/```[\s\S]*?```/gm, '');

    // Remove images
    text = text.replace(/!\[.+?\]\(.+?\)/g, '');

    // Remove links but keep text
    text = text.replace(/\[(.+?)\]\(.+?\)/g, '$1');

    // Remove inline code but keep text
    text = text.replace(/`(.+?)`/g, '$1');

    // Remove bold/italic but keep text
    text = text.replace(/\*\*(.+?)\*\*/g, '$1');
    text = text.replace(/\*(.+?)\*/g, '$1');
    text = text.replace(/__(.+?)__/g, '$1');
    text = text.replace(/_(.+?)_/g, '$1');

    // Remove headers markers
    text = text.replace(/^#{1,6}\s+/gm, '');

    // Remove blockquote markers
    text = text.replace(/^>\s+/gm, '');

    // Remove list markers
    text = text.replace(/^[-*+]\s+/gm, '');
    text = text.replace(/^\d+\.\s+/gm, '');

    // Split by whitespace and count
    const words = text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    return words.length;
  }

  getReadingTime(markdown: string, wordsPerMinute?: number): number {
    const wordCount = this.getWordCount(markdown);
    const wpm = wordsPerMinute ?? DEFAULT_WPM;
    return Math.ceil(wordCount / wpm);
  }

  private buildHierarchy(flatToc: TOCEntry[]): TOCEntry[] {
    const root: TOCEntry[] = [];
    const stack: TOCEntry[] = [];

    for (const entry of flatToc) {
      // Pop entries from stack that are at same or higher level
      while (stack.length > 0 && stack[stack.length - 1]!.level >= entry.level) {
        stack.pop();
      }

      if (stack.length === 0) {
        // Top level entry
        root.push(entry);
      } else {
        // Child entry
        const parent = stack[stack.length - 1]!;
        entry.parent = parent;
        parent.children.push(entry);
      }

      stack.push(entry);
    }

    return root;
  }

  private applyBaseUrl(html: string, baseUrl: string): string {
    // Simple implementation - would need proper HTML parsing for production
    return html.replace(
      /href="(?!http|https|\/\/|#)(.+?)"/g,
      `href="${baseUrl}$1"`
    );
  }

  private applyCssClasses(
    html: string,
    cssClasses: NonNullable<RenderOptions['cssClasses']>
  ): string {
    if (cssClasses.table) {
      html = html.replace(/<table>/g, `<table class="${cssClasses.table}">`);
    }
    if (cssClasses.img) {
      html = html.replace(/<img /g, `<img class="${cssClasses.img}" `);
    }
    if (cssClasses.code) {
      html = html.replace(/<code>/g, `<code class="${cssClasses.code}">`);
    }
    if (cssClasses.blockquote) {
      html = html.replace(
        /<blockquote>/g,
        `<blockquote class="${cssClasses.blockquote}">`
      );
    }
    return html;
  }
}
